import { Event } from '../types';

const AU_STATE_ALIASES: Array<[string, string[]]> = [
  ['ACT', ['ACT', 'AUSTRALIAN CAPITAL TERRITORY']],
  ['NSW', ['NSW', 'NEW SOUTH WALES']],
  ['NT', ['NT', 'NORTHERN TERRITORY']],
  ['QLD', ['QLD', 'QUEENSLAND']],
  ['SA', ['SA', 'SOUTH AUSTRALIA']],
  ['TAS', ['TAS', 'TASMANIA']],
  ['VIC', ['VIC', 'VICTORIA']],
  ['WA', ['WA', 'WESTERN AUSTRALIA']],
];

const EVENT_SOURCE_BY_PATH: Record<string, string> = {
  '/api/calendar-events': 'shows',
  '/api/competitions': 'pokemon',
  '/api/onepiece-competitions': 'onepiece',
  '/api/magic-competitions': 'magic',
  '/api/riftbound-competitions': 'riftbound',
  '/api/lorcana-competitions': 'lorcana',
  '/api/fleshandblood-competitions': 'fleshandblood',
};

const SUPPORTED_EVENT_SOURCE_KEYS = new Set(Object.values(EVENT_SOURCE_BY_PATH));

export const getEventSourceKey = (endpoint: string): string => {
  try {
    const path = new URL(endpoint, 'https://www.tcgcalendar.com.au').pathname;
    return EVENT_SOURCE_BY_PATH[path] || '';
  } catch {
    return '';
  }
};

const STATE_TOKEN_ALTERNATION = [
  'AUSTRALIAN CAPITAL TERRITORY',
  'NEW SOUTH WALES',
  'NORTHERN TERRITORY',
  'SOUTH AUSTRALIA',
  'WESTERN AUSTRALIA',
  'QUEENSLAND',
  'TASMANIA',
  'VICTORIA',
  'ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA',
].join('|');

const exactStateCode = (value: string): string => {
  for (const [code, aliases] of AU_STATE_ALIASES) {
    if (aliases.includes(value)) return code;
  }
  return '';
};

const stateCodeFromValue = (value: unknown): string => {
  if (typeof value !== 'string') return '';

  const normalised = value.trim().toUpperCase();
  if (!normalised) return '';

  const exact = exactStateCode(normalised);
  if (exact) return exact;

  // Australian addresses conventionally place the state immediately before
  // the postcode. Prefer that token, otherwise use the final state token so a
  // venue such as "Victoria Hall, Sydney NSW" resolves to NSW.
  const beforePostcode = normalised.match(
    new RegExp(`\\b(${STATE_TOKEN_ALTERNATION})\\b[.,]?\\s*\\d{4}\\b`)
  );
  if (beforePostcode) return exactStateCode(beforePostcode[1]);

  const matches = normalised.match(new RegExp(`\\b(${STATE_TOKEN_ALTERNATION})\\b`, 'g'));
  return matches?.length ? exactStateCode(matches[matches.length - 1]) : '';
};

/**
 * Resolve an Australian state code from the shapes used by each event feed.
 * Explicit state fields win, followed by region, address, location and tags.
 */
export const getEventStateCode = (event: any): string => {
  const extended = event?.extendedProps ?? {};
  const values = [
    event?.canonical?.state,
    extended?.canonical?.state,
    extended?.stateCode,
    event?.stateCode,
    extended?.state,
    event?.state,
    extended?.region,
    event?.region,
    extended?.address,
    event?.address,
    extended?.location,
    event?.location,
    ...(Array.isArray(extended?.tags) ? extended.tags : [extended?.tags]),
    ...(Array.isArray(event?.tags) ? event.tags : [event?.tags]),
    event?.title,
  ];

  for (const value of values) {
    const stateCode = stateCodeFromValue(value);
    if (stateCode) return stateCode;
  }

  return '';
};

const EVENT_IMAGE_MAP: Record<string, string> = {
  'league cup vg': '/play-pokemon-vgcs.png',
  'league challenge vg': '/play-pokemon-vgcs.png',
  'league cup': '/pokemon_league_logo_en.png',
  'league challenge': '/pokemon_league_logo_en.png',
  'pre release': '/pokemon-prerelease.png',
  'go challenge': '/play-pokemon-logo-GO.png',
  'go cup': '/play-pokemon-logo-GO.png',
};

export const getEventImage = (title: string): string | null => {
  const lowerTitle = title.toLowerCase();
  
  for (const [key, image] of Object.entries(EVENT_IMAGE_MAP)) {
    if (lowerTitle.includes(key)) {
      return image;
    }
  }
  
  return null;
};

export const formatEventDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-AU', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * For all-day events, Google Calendar uses exclusive end dates
 * (e.g. a 1-day event on Feb 15 has end: "2026-02-16").
 * Date-only strings (no "T") indicate all-day events; an explicit
 * allDay flag also marks a timestamp-shaped end (e.g. "...T00:00:00") as all-day.
 * This returns the inclusive end date string for display purposes.
 */
export const getInclusiveEnd = (endDate: string | Date | null | undefined, allDay?: boolean): string => {
  if (!endDate) return '';
  const dateStr = typeof endDate === 'string' ? endDate : endDate.toISOString();
  const isAllDay = allDay === true || !dateStr.includes('T');
  if (!isAllDay) return dateStr;
  const d = new Date(`${dateStr.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return '';
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
};

const toDateString = (val: unknown): string => {
  if (!val) return '';
  if (typeof val === 'string') return val;
  if (val instanceof Date) return val.toISOString();
  return String(val);
};

export const transformEventPayload = (rawEvent: any): Event => {
  const extended = rawEvent?.extendedProps ?? {};
  const tags = extended?.tags ?? rawEvent?.tags ?? [];
  const rawStart = typeof extended?.rawStart === 'string' && extended.rawStart
    ? extended.rawStart
    : toDateString(rawEvent?.start);
  const rawEnd = typeof extended?.rawEnd === 'string' && extended.rawEnd
    ? extended.rawEnd
    : toDateString(rawEvent?.end);
  const eventDetailPath = typeof extended?.eventDetailPath === 'string'
    ? extended.eventDetailPath
    : undefined;
  const allDay = typeof rawEvent?.allDay === 'boolean'
    ? rawEvent.allDay
    : (typeof extended?.allDay === 'boolean' ? extended.allDay : undefined);
  // Immutable slug override (organiser events and repaired-title legacy URLs).
  // Must survive transformation:
  // EventDetail matches /events/<slug> against TRANSFORMED events, so dropping
  // it here would break organiser event resolution client-side. Read top-level
  // first, then extendedProps (FullCalendar relocates unknown top-level fields
  // into EventApi.extendedProps, so after a calendar click only the
  // extendedProps copy survives), and write it back to BOTH places so the slug
  // survives any further round trip through FullCalendar.
  const slug = typeof rawEvent?.slug === 'string' && rawEvent.slug
    ? rawEvent.slug
    : (typeof extended?.slug === 'string' && extended.slug ? extended.slug : undefined);
  const dedupeKey = typeof rawEvent?.dedupeKey === 'string' && rawEvent.dedupeKey
    ? rawEvent.dedupeKey
    : (typeof extended?.dedupeKey === 'string' && extended.dedupeKey ? extended.dedupeKey : undefined);
  const stateCode = getEventStateCode(rawEvent);
  const canonical = rawEvent?.canonical ?? extended?.canonical;
  const timeZone = extended?.timeZone ?? extended?.timezone
    ?? rawEvent?.timeZone ?? rawEvent?.timezone ?? canonical?.timezone ?? '';
  const status = rawEvent?.status ?? extended?.status ?? canonical?.status;
  const aggregateFeed = typeof rawEvent?.feed === 'string' ? rawEvent.feed.trim().toLowerCase() : '';
  // Only game feeds with an existing save namespace can inherit an organiser
  // submission's game; yugioh and other remain shows so saves stay accepted.
  const aggregateGame = aggregateFeed === 'organiser'
    ? String(extended?.game ?? rawEvent?.game ?? '').trim().toLowerCase()
    : '';
  const aggregateSourceKey = aggregateFeed === 'calendar'
    ? 'shows'
    : aggregateFeed === 'organiser'
      ? (SUPPORTED_EVENT_SOURCE_KEYS.has(aggregateGame) ? aggregateGame : 'shows')
      : aggregateFeed;

  return {
    id: rawEvent?.id ?? '',
    title: rawEvent?.title ?? '',
    description: rawEvent?.description ?? extended?.description ?? '',
    ...(slug ? { slug } : {}),
    ...(dedupeKey ? { dedupeKey } : {}),
    ...(canonical ? { canonical } : {}),
    ...(timeZone ? { timezone: timeZone } : {}),
    ...(status ? { status } : {}),
    ...(typeof allDay === 'boolean' ? { allDay } : {}),
    start: rawStart,
    end: rawEnd,
    url: rawEvent?.url ?? extended?.descriptionUrl ?? '',
    extendedProps: {
      ...(slug ? { slug } : {}),
      ...(dedupeKey ? { dedupeKey } : {}),
      location: extended?.location ?? rawEvent?.location ?? '',
      description: extended?.description ?? rawEvent?.description ?? '',
      descriptionUrl: extended?.descriptionUrl ?? rawEvent?.descriptionUrl ?? '',
      offerUrl: extended?.offerUrl ?? rawEvent?.offerUrl ?? '',
      ticketCost: extended?.ticketCost ?? rawEvent?.ticketCost ?? '',
      tags: Array.isArray(tags) ? tags : typeof tags === 'string' ? [tags] : [],
      image: extended?.image ?? rawEvent?.image ?? '',
      shop: extended?.shop ?? rawEvent?.shop ?? '',
      venueName: extended?.venueName ?? rawEvent?.venueName ?? '',
      address: extended?.address ?? rawEvent?.address ?? '',
      suburb: extended?.suburb ?? rawEvent?.suburb ?? '',
      locality: extended?.locality ?? rawEvent?.locality ?? '',
      city: extended?.city ?? rawEvent?.city ?? '',
      format: extended?.format ?? rawEvent?.format ?? '',
      series: extended?.series ?? rawEvent?.series ?? '',
      region: extended?.region ?? rawEvent?.region ?? '',
      stateCode,
      timeZone,
      ...(canonical ? { canonical } : {}),
      ...(status ? { status } : {}),
      startInstant: extended?.startInstant ?? rawEvent?.startInstant ?? canonical?.startInstant ?? '',
      endInstant: extended?.endInstant ?? rawEvent?.endInstant ?? canonical?.endInstant ?? '',
      postcode: extended?.postcode ?? rawEvent?.postcode ?? canonical?.postcode ?? '',
      latitude: extended?.latitude ?? rawEvent?.latitude ?? canonical?.latitude ?? undefined,
      longitude: extended?.longitude ?? rawEvent?.longitude ?? canonical?.longitude ?? undefined,
      sourceUpdatedAt: extended?.sourceUpdatedAt ?? rawEvent?.sourceUpdatedAt ?? canonical?.sourceUpdatedAt ?? '',
      lastVerifiedAt: extended?.lastVerifiedAt ?? rawEvent?.lastVerifiedAt ?? canonical?.lastVerifiedAt ?? '',
      // Aggregated endpoints tag each event with its original feed. Preserve
      // that identity when a custom endpoint (such as related set events)
      // returns a mixture of games instead of deriving one source from the
      // endpoint URL.
      sourceKey: extended?.sourceKey ?? rawEvent?.sourceKey ?? aggregateSourceKey,
      game: extended?.game ?? rawEvent?.game ?? '',
      eventType: extended?.eventType ?? rawEvent?.eventType ?? '',
      ...(typeof allDay === 'boolean' ? { allDay } : {}),
      ...(extended?.performer ?? rawEvent?.performer
        ? { performer: extended?.performer ?? rawEvent?.performer }
        : {}),
      ...(extended?.organizer ?? rawEvent?.organizer
        ? { organizer: extended?.organizer ?? rawEvent?.organizer }
        : {}),
      ...(rawStart ? { rawStart } : {}),
      ...(rawEnd ? { rawEnd } : {}),
      ...(eventDetailPath ? { eventDetailPath } : {}),
    },
  };
};
